import React from "react";
import { useTranslation } from "react-i18next";
import { ProcessingItem, processingRows, preciseNumber } from "./processing-presentation";
import { fmt } from "./ui";

export default function ProcessingBreakdown({ items, expectedTotal }: { items?: ProcessingItem[]; expectedTotal: number }) {
  const { t } = useTranslation("supplierPricing");
  const rows = processingRows(items);
  const complete = rows.every(row => row.amount !== null && Number.isFinite(row.amount));
  const sum = rows.reduce((total, row) => total + (row.amount || 0), 0);
  const mismatch = rows.length > 0 && complete && Math.abs(sum - expectedTotal) > 0.005;
  const hasPrecision = rows.some(row => row.exact !== null && row.amount !== null &&
    (row.amount === 0 ? row.exact !== 0 : Math.abs(row.exact - row.amount) > 1e-10));
  if (!rows.length) return expectedTotal ? <p className="eb-pr-processing-note">{t("order.processingDetailsUnavailable")}</p> : null;
  return <section className="eb-pr-processing-details" aria-label={t("order.processingDetails")}>
    {rows.map(({ item, charge, quantity, rate, amount, exact, missing }, index) => {
      const shape = item.shape_id ?? item.shapeId;
      const name = item.shape_name ?? item.shapeName ?? item.name;
      const number = item.shape_number ?? item.shapeNumber;
      const line = item.row_number ?? item.rowNumber;
      const unrounded = exact !== null && Number.isFinite(exact) ? exact : amount;
      const showBilled = exact !== null && amount !== null &&
        (amount === 0 ? exact !== 0 : Math.abs(exact - amount) > 1e-10);
      const unit = t(charge === "unit" ? "order.units" : "units.ton");
      const type = t(charge === "unit" ? "processing.unitPrice" : `processing.${charge}`);
      return <article className="eb-pr-processing-group" key={item.id ?? index}>
        <strong>{type}</strong>
        <p>{line != null ? t("order.processingRow", { row: line }) : t("order.processingRowUnavailable")}
          {" · "}{name || (shape != null ? t("order.processingShape", { shape }) : t("order.processingShapeUnavailable"))}
          {name && shape != null && <> ({t("order.processingShape", { shape })})</>}
          {number && number !== String(shape) && <> · {number}</>}
        </p>
        {missing ? <p role="alert">{t("order.processingMissing")}</p> : <>
          <p className="eb-pr-processing-equation">
            <bdi>{quantity !== null ? preciseNumber(quantity) : "—"}</bdi> {unit}
            {" × "}<bdi>{rate !== null ? `${preciseNumber(rate, 2)} ₪` : t("order.processingRateUnavailable")}</bdi>
            {" = "}<bdi>{unrounded !== null ? `${preciseNumber(unrounded, 2)} ₪` : "—"}</bdi>
          </p>
          {exact === null && amount === 0 && <p>{t("order.processingPrecisionUnavailable")}</p>}
          {showBilled && amount !== null &&
            <p>{t("order.processingBilledAmount")} <bdi>{fmt(amount)} ₪</bdi></p>}
        </>}
      </article>;
    })}
    {hasPrecision && <p className="eb-pr-processing-note">{t("order.processingPrecisionExplanation")}</p>}
    {mismatch && <p role="alert">{t("order.processingMismatch")}</p>}
  </section>;
}
